import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {URLS, APIS, SF_ORG_MGMT_SRCH, SG_ORG_MGMT_LIST, SG_ORG_MGMT_EXCEL_LIST } from "@/tra/tancis/ptli/usr/org/services/PtliOrgService";
import dayjs from "dayjs";

/*
 * Organization Management List
 * !== 기관 관리 목록 ==!
 */
export const PTLI0916001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    }); 
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const [load, setLoad] = useState(false);

    useResource({
        defaultSchema: [{ area: "orgCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization Management List Search !== 기관 관리 목록 검색 ==!
        orgList: useForm({
            defaultSchema: SF_ORG_MGMT_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Organization Management List !== 기관 관리 목록 ==!
        orgList: useGrid({
            defaultSchema: SG_ORG_MGMT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        // Organization Management List !== 기관 관리 목록 ==!
        orgExcelList: useGrid({
            defaultSchema: SG_ORG_MGMT_EXCEL_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check Organization List !==  기관 관리 목록 조회==!
        getOrgList: useFetch({
            api: (page = grid.orgList.page) => APIS.getOrgList(form.orgList.getValues(), page, grid.orgList.size),
            enabled: comnUtils.isEmpty(form.orgList.errors),
            key: [grid.orgList.page, grid.orgList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.orgList.getValues(),
                    page: grid.orgList.page,
                    size: grid.orgList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
        getOrgExcelList: useFetch({  
            api: (data: any) => (load ? APIS.getOrgExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.orgList.errors),
            onSuccess: () => {
                if (load) {
                    grid.orgExcelList.exportExcel({
                        excelName: `${t("L_INT_PTL")}_${t("L_OGA")}_${t("L_MGMT_LST")}_${dayjs().format(
                            "DD/MM/YYYY HHmm",
                        )}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
         }),
    };
    const render = {
        cell: {
            orgNm: (props: any) => {
                const { rowValues, value } = props;
                return <Link to={`/ptli/usr/org/ptli0916004q/${rowValues.orgCd}`}>{value}</Link>;
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: (page: number = 0) => {
            grid.orgList.setPage(page);
            fetch.getOrgList.fetch(page);
        },
        //Click Registration Button  !== 등록 버튼 클릭 ==!
        onNewBtnClick: () => navigate(URLS.ptli0916002s),
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick(pgeStore?.page);
    }, []);

    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getOrgExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgList.schema.orgCd} />
                            <Group.Control {...form.orgList.schema.orgNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgList.schema.lpcoIssuYn} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.orgList.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    setLoad(true);
                                }}
                            ></Button>
                        </Layout.Left>
                    </Layout>

                    <Group.Section>
                        <Grid {...grid.orgList.grid} data={fetch.getOrgList.data?.orgList} render={render} />
                    </Group.Section>

                    
                    <Group hidden>
                        <Grid {...grid.orgExcelList.grid} data={fetch.getOrgExcelList.data?.ogaExcelList} />
                    </Group>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button role={"new"} onClick={handler.onNewBtnClick} />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
